/*!
 * Copyright 2017 - 2020 by ChartIQ, Inc.
 * All rights reserved.
 */

import Logger from "../../clients/logger";
import * as util from "../../common/util";

export const SINGLE_PATH_OPERATIONS: string[] = ["setValue", "removeProperty", "logMessage"];

/**
 * Class to process deprecated config operations, which essentially is updating old config format to newer config format (as defined by config schema).
 *
 * @class DeprecatedConfigUtil
 */
class DeprecatedConfigUtil {
	/**
	 * Returns child property value from parent object.  The child property may include an array reference (e.g. someChild[4})
	 *
	 * @private
	 * @param {*} parentObject the parent object
	 * @param {*} child child property to return value for (may include an array reference)
	 * @param {*} path the overall path to log if error
	 * @returns
	 * @memberof DeprecatedConfigUtil
	 */
	private getChildPropertyValue(parentObject, child, path) {
		let value;
		if (child.includes("[")) {
			child = child.replace("]", "[");
			let pathItems = child.split("[");
			if (pathItems.length !== 3) {
				throw new Error(`"illegal array syntax ${child} within ${path} so skipping`);
			} else {
				let arrayPropertyName = pathItems[0];
				let arrayIndex = pathItems[1];
				value = parentObject[arrayPropertyName][arrayIndex];
				if (value === undefined) {
					throw new Error(`"undefined array element ${arrayPropertyName}[${arrayIndex}] within ${path} so skipping`);
				}
			}
		} else {
			value = parentObject[child];
		}
		return value;
	}

	/**
	 * Sets a child property value on a parent object.  The child property may include an array reference (e.g. someChild[4})
	 *
	 * @private
	 * @param {*} parentObject the parent object
	 * @param {*} child child property to set value for (may include an array reference)
	 * @param {*} newValue the new value to set child property to
	 * @param {*} clone if true and new value is an obect, then clone the object before using to set new value
	 * @param {*} path the overall path to log if error
	 * @memberof DeprecatedConfigUtil
	 */
	private setChildPropertyValue(parentObject, child, newValue, clone, path) {
		let value;

		if (typeof newValue === "object" && clone) {
			value = util.clone(newValue);
		} else {
			value = newValue;
		}

		if (child.includes("[")) {
			child = child.replace("]", "[");
			let pathItems = child.split("[");
			if (pathItems.length !== 3) {
				throw new Error(`"illegal array syntax ${child} within ${path} so skipping`);
			} else {
				let arrayPropertyName = pathItems[0];
				let arrayIndex = pathItems[1];
				if (parentObject[arrayPropertyName][arrayIndex] !== undefined)
					parentObject[arrayPropertyName][arrayIndex] = value;
				else {
					throw new Error(`"undefined array element ${arrayPropertyName}[${arrayIndex}] within ${path} so skipping`);
				}
			}
		} else {
			parentObject[child] = value;
		}
	}

	/**
	 * Return a config property value for a given path
	 *
	 * @private
	 * @param {*} manifestConfig object containing config
	 * @param {*} path the path to find value for
	 * @returns {any} the value found, which may be an object, or undefined if no corresponding value
	 * @memberof DeprecatedConfigUtil
	 */
	private findConfigPropertyValue(manifestConfig, path): any {
		let lastPathPosition;
		let index;
		let pathItems;
		let undefined;
		if (path) {
			lastPathPosition = manifestConfig;
			pathItems = path.split(".");
			for (index = 0; index < pathItems.length; index++) {
				lastPathPosition = this.getChildPropertyValue(lastPathPosition, pathItems[index], path);
				if (typeof lastPathPosition !== "object") {
					break;
				}
			}
		}
		// if couldn't fully traverse the path then value is undefined
		if (index < pathItems.length - 1) {
			lastPathPosition = undefined;
		}
		Logger.system.debug("DeprecatedConfigUtil.findConfigParent", path, lastPathPosition);
		return lastPathPosition;
	}

	/**
	 * Returns the parent object for a complete path (i.e. the object that contains last/leaf property in the path)
	 *
	 * @private
	 * @param {*} manifestConfig object containing config
	 * @param {*} path
	 * @returns {object}
	 * @memberof DeprecatedConfigUtil
	 */
	private findConfigParent(manifestConfig, path): object {
		let pathItems = path.split(".");
		let lastPathPosition = manifestConfig;
		for (let i = 0; i < pathItems.length - 1; i++) {
			// if path property doesn't exist in config, then create it (except for arrays which must preexist)
			if (!pathItems[i].includes("[") && !lastPathPosition[pathItems[i]]) {
				lastPathPosition[pathItems[i]] = {};
			}
			lastPathPosition = this.getChildPropertyValue(lastPathPosition, pathItems[i], path);
		}
		Logger.system.debug("DeprecatedConfigUtil.findConfigParent", path, lastPathPosition);
		return lastPathPosition;
	}

	/**
	 * Returns the last/leaf property of a config path
	 *
	 * @private
	 * @param {*} path
	 * @returns {string}
	 * @memberof DeprecatedConfigUtil
	 */
	private getLastPathProperty(path): string {
		let pathItems = path.split(".");
		let lastProperty = pathItems.pop();
		Logger.system.debug("DeprecatedConfigUtil.getLastPathProperty", path, lastProperty);
		return lastProperty;
	}

	/**
	 * copy a source object's contents to a target object, adding to as opposed to replacing target value
	 *
	 * @private
	 * @param {*} targetObject
	 * @param {*} sourceObject
	 * @param {*} clone
	 * @memberof DeprecatedConfigUtil
	 */
	private copyObjectProperties(targetObject, sourceObject, clone) {
		let value;
		if (typeof sourceObject === "object" && clone) {
			value = util.clone(sourceObject);
		} else {
			value = sourceObject;
		}
		Object.assign(targetObject, value);
	}

	/**
	 * Returns log function to use for logging results of an operation
	 *
	 * @private
	 * @param {string} userMessageType deprecated-config operation's message type which determines how to log the message (e.g. error, warn, log)
	 * @returns {*} returns log function to use (e.g. logger.system.error, logger.system.warn, logger.system.log)
	 * @memberof DeprecatedConfigUtil
	 */
	private systemLog(userMessageType: string): Function {
		let loggerEntryPoint = Logger.system.warn;
		if (userMessageType === "error") {
			loggerEntryPoint = Logger.system.error;
		}
		return loggerEntryPoint;
	}

	/**
	 * Handles moveProperty operation, which optionally move old property location to a new location
	 *
	 * @private
	 * @param {*} manifestConfig object containing config
	 * @param {*} depRequest object defining a single deprecated-config request/operation
	 * @memberof DeprecatedConfigUtil
	 */
	private moveConfigProperty(manifestConfig, depRequest) {
		let oldConfigPropertyValue = this.findConfigPropertyValue(manifestConfig, depRequest.oldProperty);
		if (typeof oldConfigPropertyValue !== "undefined") {
			let newConfigParent = this.findConfigParent(manifestConfig, depRequest.newProperty);
			let newProperty = this.getLastPathProperty(depRequest.newProperty);
			if (
				depRequest.addToExistingObject &&
				typeof newConfigParent[newProperty] === "object" &&
				!Array.isArray(newConfigParent[newProperty])
			) {
				this.copyObjectProperties(newConfigParent[newProperty], oldConfigPropertyValue, depRequest.clone);
			} else {
				this.setChildPropertyValue(
					newConfigParent,
					newProperty,
					oldConfigPropertyValue,
					depRequest.clone,
					depRequest.newProperty
				);
			}

			this.systemLog(depRequest.userMessageType)(
				`${depRequest.versionDeprecated} deprecated config ${depRequest.oldProperty} was found and updated to ${depRequest.newProperty}`
			);

			if (depRequest.removeOld) {
				let oldConfigParent = this.findConfigParent(manifestConfig, depRequest.oldProperty);
				delete oldConfigParent[this.getLastPathProperty(depRequest.oldProperty)];
				Logger.system.log(`"DeprecatedConfig: ${depRequest.oldProperty} removed`);
			}
		} else {
			Logger.system.warn(`"DeprecatedConfig: ${depRequest.oldProperty} not found so skipped`, depRequest);
		}
	}

	/**
	 * Handles setValue operation, which optionally sets config property to specified value.
	 *
	 * @private
	 * @param {*} manifestConfig object containing config
	 * @param {*} depRequest object defining a single deprecated-config request/operation
	 * @memberof DeprecatedConfigUtil
	 */
	private setPropertyValue(manifestConfig, depRequest) {
		let oldConfigPropertyValue = this.findConfigPropertyValue(manifestConfig, depRequest.property);
		if (!depRequest.ifOldValue || depRequest.ifOldValue === oldConfigPropertyValue) {
			let newConfigParent = this.findConfigParent(manifestConfig, depRequest.property);
			let newProperty = this.getLastPathProperty(depRequest.property);
			newConfigParent[newProperty] = depRequest.newValue;
			this.systemLog(depRequest.userMessageType)(
				`${depRequest.versionDeprecated} deprecated config value for ${depRequest.property} set to ${depRequest.newValue}`
			);
		}
	}

	/**
	 * Handles removeProperty operation, which removes a property from config.
	 *
	 * @private
	 * @param {*} manifestConfig object containing config
	 * @param {*} depRequest object containing a single deprecated-config remove request
	 * @memberof DeprecatedConfigUtil
	 */
	private removeProperty(manifestConfig, depRequest) {
		let configParent = this.findConfigParent(manifestConfig, depRequest.property);
		let propertyToRemove = this.getLastPathProperty(depRequest.property);
		if (typeof configParent[propertyToRemove] !== "undefined") {
			delete configParent[propertyToRemove];
			this.systemLog(depRequest.userMessageType)(
				`${depRequest.versionDeprecated} deprecated config property ${depRequest.property} removed`
			);
		}
	}

	/**
	 * Return an array of property names that corresponds to the first wildcard ("*") defined in the config path
	 *
	 * @private
	 * @param {*} manifestConfig object containing config
	 * @param {*} path
	 * @returns {*}
	 * @memberof DeprecatedConfigUtil
	 */
	private getWildCardListItems(manifestConfig, path): any {
		let list: any[] = [];
		let lastPathPosition = manifestConfig;
		if (path.includes("*")) {
			let pathItems = path.split(".");
			for (let i = 0; i < pathItems.length; i++) {
				if (pathItems[i] === "*") {
					if (typeof lastPathPosition === "object") {
						list = Object.keys(lastPathPosition);
					}
					break;
				}
				lastPathPosition = lastPathPosition[pathItems[i]];
				if (typeof lastPathPosition !== "object") {
					break;
				}
			}
		}
		return list;
	}

	/**
	 * Return true if any wildcards used in the two input paths are mismatch (i.e. are illegal/incompatible)
	 *
	 * Here's what's legal (which results in false being returned):
	 * 	1) if wildcard in both paths, then both wildcard portions must be identical
	 * 	2) if wildcard is only in one path, then it must be within the newProperty path
	 *  3) if no wildcards used in either path, then legal (there is no mismatch)
	 *
	 * @private
	 * @param {*} oldProperty path of old config property (i.e. the deprecated property)
	 * @param {*} newProperty path of new config property (i.e. the new location)
	 * @returns {boolean}
	 * @memberof DeprecatedConfigUtil
	 */
	private wildCardMismatch(oldProperty, newProperty): boolean {
		const NOT_FOUND = -1;
		let mismatch;

		let oldPropertyWildCardIndex = oldProperty.indexOf("*");
		let newPropertyWildCardIndex = oldProperty.indexOf("*");

		if (oldPropertyWildCardIndex === NOT_FOUND) {
			mismatch = false;
		} else {
			let firstWildCardPath = oldProperty.substring(0, oldPropertyWildCardIndex);
			let secondWildCardPath = newProperty.substring(0, newPropertyWildCardIndex);
			mismatch = firstWildCardPath !== secondWildCardPath;
		}

		return mismatch;
	}

	/**
	 * Log message if deprecated config property exists.
	 *
	 * @private
	 * @param {*} manifestConfig object containing config
	 * @param {*} depRequest object defining a single operation
	 * @memberof DeprecatedConfigUtil
	 */
	private displayDeprecationMessage(manifestConfig, depRequest) {
		let propertyValue = this.findConfigPropertyValue(manifestConfig, depRequest.property);
		if (propertyValue != undefined) {
			this.systemLog(depRequest.userMessageType)(
				`${depRequest.versionDeprecated} deprecated config found for ${depRequest.property}. ${depRequest.userMessage}`
			);
		}
	}

	private deprecatedRequest(depRequest, manifestConfig) {
		if (Array.isArray(depRequest.set)) {
			let theSet = depRequest.set;
			delete depRequest.set;
			for (let i = 0; i < theSet.length; i++) {
				let propertyList = Object.keys(theSet[i]);
				for (let j = 0; j < propertyList.length; j++) {
					depRequest[propertyList[j]] = theSet[i][propertyList[j]];
				}
				this.processDeprecatedProperty(context, manifestConfig, depRequest);
			}
		} else {
			Logger.system.log("DeprecatedConfigUtil.processDeprecatedProperty skipping illegal set operation", depRequest);
		}
	}

	private singlePathOperation(depRequest, manifestConfig) {
		// get list of possible wildcard values (or empty list if no wildcard)
		const wildCardList = this.getWildCardListItems(manifestConfig, depRequest.property);
		// if no wildcards then process normally
		if (wildCardList.length === 0) {
			switch (depRequest.operation) {
				case "setValue":
					Logger.system.debug(`DeprecatedConfigUtil.setValue for property ${depRequest.property}`, depRequest);
					this.setPropertyValue(manifestConfig, depRequest);
					break;
				case "removeProperty":
					Logger.system.debug(`DeprecatedConfigUtil.removeProperty for ${depRequest.property}`, depRequest);
					this.removeProperty(manifestConfig, depRequest);
					break;
				case "logMessage":
					Logger.system.debug(`DeprecatedConfigUtil.logMessageOnly for ${depRequest.property}`, depRequest);
					this.displayDeprecationMessage(manifestConfig, depRequest);
				default:
			}
			// else wildcards so process each after filling in wildcard value for each possibility
		} else {
			let path;
			let originalProperty = depRequest.property.slice();
			while ((path = wildCardList.pop())) {
				depRequest.property = originalProperty.replace(".*.", `.${path}.`);
				// make a recursive call back into this function to handle nested wildcards
				this.processDeprecatedProperty(context, manifestConfig, depRequest);
			}
		}
	}

	private moveProperty(depRequest, manifestConfig) {
		if (this.wildCardMismatch(depRequest.oldProperty, depRequest.newProperty)) {
			Logger.system.warn(
				`DeprecatedConfig: wildCards are mismatched for ${depRequest.oldProperty} and ${depRequest.newProperty} so ignoring`,
				depRequest
			);
		} else {
			// get list of possible wildcard values (or empty list if no wildcard)
			const wildCardList = this.getWildCardListItems(manifestConfig, depRequest.newProperty);
			// if no wildcards then process normally
			if (wildCardList.length === 0) {
				Logger.system.debug(
					`DeprecatedConfigUtil.moveProperty from ${depRequest.oldProperty} to ${depRequest.newProperty}`,
					depRequest
				);
				this.moveConfigProperty(manifestConfig, depRequest);
			} else {
				let path;
				let originalOldProperty = depRequest.oldProperty.slice();
				let originalNewProperty = depRequest.newProperty.slice();
				while ((path = wildCardList.pop())) {
					depRequest.oldProperty = originalOldProperty.replace(".*.", `.${path}.`);
					depRequest.newProperty = originalNewProperty.replace(".*.", `.${path}.`);
					// make a recursive call back into this function to handle nested wildcards
					this.processDeprecatedProperty(context, manifestConfig, depRequest);
				}
			}
		}
	}

	/**
	 * Process one deprecated-config request. If the request's operation includes wildcards, then this function is recursively invoked for each wildcard case.
	 *
	 * @private
	 * @param {*} context defines the current context running within
	 * @param {*} manifestConfig object containing config
	 * @param {*} depRequest object defining a single deprecated-config request/operation
	 * @memberof DeprecatedConfigUtil
	 */
	private processDeprecatedProperty(context, manifestConfig, depRequest) {
		try {
			if (depRequest.when !== undefined && !depRequest.when[context]) {
				Logger.system.log(
					"DeprecatedConfigUtil.processDeprecatedProperty skipping item because wrong context",
					depRequest
				);
			} else if (depRequest.set) {
				this.deprecatedRequest(depRequest, manifestConfig);
			} else if (SINGLE_PATH_OPERATIONS.includes(depRequest.operation)) {
				this.singlePathOperation(depRequest, manifestConfig);
			} else if (depRequest.operation === "moveProperty") {
				this.moveProperty(depRequest, manifestConfig);
			} else {
				Logger.system.warn("DeprecatedConfigUtil.processDeprecatedProperty skipping unknown operation", depRequest);
			}
		} catch (err) {
			Logger.system.error(`DeprecatedConfig: error caught: ${err}`, depRequest);
		}
	}

	/**
	 * Run through all the finsemble.deprecatedConfig operations defined in config, applying each one that matches the input context
	 *
	 * @param {*} context the execution context, which specifies which operations should be applied.
	 * 		One of three context values:
	 * 			"bootConfig" is the minimum config need to boot, pulled together by the service Manager
	 * 			"mainConfig" if the full main configure pulled together by the config service
	 * 			"dynamicConfig" if the full main config after a dynamic change request was sent to the config service
	 * 			See "Handling Deprecated Config" tutorial for a higher-level explanation of context.
	 * @param {*} manifest manifest config including finsemble config
	 * @returns
	 * @memberof DeprecatedConfigUtil
	 */
	modifyConfigBasedOnDeprecatedDefinitions(context, manifest) {
		if (Array.isArray(manifest.finsemble.deprecatedConfig)) {
			let depRequests = manifest.finsemble.deprecatedConfig;
			for (let i = 0; i < depRequests.length; i++) {
				Logger.system.debug(
					"DeprecatedConfigUtil.modifyConfigBasedOnDeprecatedDefinitions processing",
					context,
					depRequests[i]
				);
				this.processDeprecatedProperty(context, manifest, depRequests[i]);
			}
		}
		return true;
	}
}

export const DeprecatedConfigUtilInstance = new DeprecatedConfigUtil();
